import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutGrid, LayoutDashboard } from "lucide-react";
import MobileCurvedBottomNav from "./mobile/MobileCurvedBottomNav";

function NavTabIcon({ icon: Icon, className }) {
  const Resolved = Icon || LayoutDashboard;
  return <Resolved className={className} />;
}

function pathMatches(pathname, to) {
  if (to === "/") return pathname === "/";
  if (to === "/production") return pathname === "/production";
  if (to.endsWith("/dashboard")) return pathname === to;
  return pathname === to || pathname.startsWith(`${to}/`);
}

function tabActive(pathname, itemOrTo) {
  if (typeof itemOrTo === "string") return pathMatches(pathname, itemOrTo);
  const item = itemOrTo || {};
  if (Array.isArray(item.matchTos) && item.matchTos.length) {
    return item.matchTos.some((to) => pathMatches(pathname, to));
  }
  return pathMatches(pathname, item.to);
}

const tabClass = (active) =>
  `flex flex-1 flex-col items-center justify-center gap-0.5 min-h-[52px] px-1 py-1.5 text-[10px] font-medium transition-colors touch-manipulation ${
    active ? "text-primary" : "text-muted-foreground active:text-foreground"
  }`;

/** Bottom navigation — curved FAB bar for single-module users; home/quick/more for others. */
export default function AppBottomNav({ mode = "modules", tabs = [], home, quickItems = [], onMore }) {
  const { pathname } = useLocation();

  if (mode === "direct" && tabs.length > 0) {
    return <MobileCurvedBottomNav tabs={tabs} />;
  }

  return (
    <nav
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-border/80 bg-card/95 backdrop-blur-md shadow-[0_-4px_24px_rgba(0,0,0,0.06)] pb-[env(safe-area-inset-bottom)]"
      data-testid="app-bottom-nav"
      aria-label="Main navigation"
    >
      <div className="flex items-stretch justify-around max-w-lg mx-auto">
        {home && (
          <NavLink to={home.to} end className={tabClass(tabActive(pathname, home))} data-testid="nav-tab-home">
            <NavTabIcon
              icon={home.icon}
              className={`h-5 w-5 ${tabActive(pathname, home) ? "stroke-[2.5px]" : ""}`}
            />
            <span>{home.shortLabel || "Home"}</span>
          </NavLink>
        )}
        {quickItems.map((item) => {
          const active = tabActive(pathname, item);
          return (
            <NavLink
              key={item.moduleId || item.to}
              to={item.to}
              end={item.to === "/"}
              className={tabClass(active)}
              data-testid={`nav-tab-${item.moduleId || item.iconKey || "item"}`}
            >
              <NavTabIcon
                icon={item.icon}
                className={`h-5 w-5 ${active ? "stroke-[2.5px]" : ""}`}
              />
              <span className="truncate max-w-[4.5rem]">{item.shortLabel || item.label.split(" ")[0]}</span>
            </NavLink>
          );
        })}
        {onMore && (
          <button
            type="button"
            onClick={onMore}
            className="flex flex-1 flex-col items-center justify-center gap-0.5 min-h-[52px] px-1 py-1.5 text-[10px] font-medium text-muted-foreground active:text-foreground touch-manipulation"
            data-testid="nav-tab-more"
            aria-label="More modules"
          >
            <LayoutGrid className="h-5 w-5" />
            <span>More</span>
          </button>
        )}
      </div>
    </nav>
  );
}
